import type { Project, Stage } from "./types";

/**
 * 1-based position of each subtask within its project's 재봉 sequence.
 *
 * The stored `sequence` runs across 준비·재봉·완성 together, so 고양이 하루's two sewing
 * tasks are 9 and 10 there. Only 재봉 is planned and shown, so "the 9th step" would name a
 * position in a list the user never sees.
 */
export function sewingStepByTask(projects: Project[]): Map<string, number> {
  return new Map(projects.flatMap(project =>
    project.subtasks
      .filter(task => task.stage === "sewing")
      .sort((a, b) => a.sequence - b.sequence)
      .map((task, index) => [task.id, index + 1] as const)));
}

export const stepLabel = (step: number | undefined) => step === undefined ? "" : `S${step}`;

/* 공정이 흐르는 순서. 머리글자는 저장된 단계 값을 그대로 쓴다 — 밑(Prep)·재봉(Sewing)·
   완성(Finish). "뒤집기"가 재봉 앞인지 뒤인지는 이름만으로는 알 수 없고, 목록에서 재봉만
   번호를 달고 있으면 나머지가 어느 단계인지 매번 세어 봐야 했다. */
export const stageOrder: Stage[] = ["prep", "sewing", "finish"];
const stageInitial: Record<Stage, string> = { prep: "P", sewing: "S", finish: "F" };

export type StageStep = { stage: Stage; index: number };

/** 세 단계 전부에 번호를 단다. 번호는 단계 안에서만 센다 — P1·S1·F1이 각각 그 단계의 첫째다. */
export function stageStepByTask(projects: Project[]): Map<string, StageStep> {
  return new Map(projects.flatMap(project =>
    stageOrder.flatMap(stage =>
      project.subtasks
        .filter(task => task.stage === stage)
        .sort((a, b) => a.sequence - b.sequence)
        .map((task, index) => [task.id, { stage, index: index + 1 }] as const))));
}

export const stageStepLabel = (step: StageStep | undefined) =>
  step === undefined ? "" : `${stageInitial[step.stage]}${step.index}`;
